import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { localizationConfig } from "./localization-config";
import { getPreferredLocale, setLocaleCookie } from "./locale-cookie";
import type { I18nBundle } from "./resources";
import { ALL_LOCALES, DEFAULT_LOCALE, type Locale } from "./locales";

const SUPPORTED_LOCALES: readonly Locale[] = ALL_LOCALES;

let activeBundle: I18nBundle;

export function initI18n(bundle: I18nBundle) {
  activeBundle = bundle;
  const locale = getPreferredLocale(SUPPORTED_LOCALES);

  i18n.on("languageChanged", (lng) => {
    if (localizationConfig.isEnabled()) {
      setLocaleCookie(lng);
    }
    if (
      lng !== DEFAULT_LOCALE && // English is bundled statically
      SUPPORTED_LOCALES.includes(lng as Locale) && // ignore unknown locales
      !i18n.hasResourceBundle(lng, "translation") // skip if already loaded (also prevents recursion from the emit below)
    ) {
      void Promise.resolve(activeBundle.loaders[lng]?.() ?? {}).then(
        (translations) => {
          i18n.addResourceBundle(lng, "translation", translations, true, true);
          i18n.emit("languageChanged", lng);
        },
      );
    }
  });

  void i18n.use(initReactI18next).init({
    resources: { [DEFAULT_LOCALE]: { translation: bundle.en } },
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    keySeparator: false,
    nsSeparator: false,
    contextSeparator: "__rc__",
    pluralSeparator: "__rp__",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

/**
 * Switch to a locale, loading its bundle first to avoid an English flash
 * during the chunk fetch. Use this instead of calling i18n.changeLanguage
 * directly from UI code.
 */
export async function setLocale(locale: Locale): Promise<void> {
  if (
    locale !== DEFAULT_LOCALE &&
    !i18n.hasResourceBundle(locale, "translation")
  ) {
    const translations = (await activeBundle.loaders[locale]?.()) ?? {};
    i18n.addResourceBundle(locale, "translation", translations, true, true);
  }
  await i18n.changeLanguage(locale);
}

export function updateLocalization(
  enabled: boolean,
  preferredLocale?: string | null,
): void {
  localizationConfig.setEnabled(enabled);
  if (!i18n.isInitialized) {
    return;
  }
  const locale = getPreferredLocale(SUPPORTED_LOCALES, preferredLocale);
  if (i18n.language !== locale) {
    void setLocale(locale);
  }
}
